// ---------------------------------------------------------------------------
// Tenant Review Provider Modes — RV-005 Phase 2 (persistence)
// ---------------------------------------------------------------------------
// Story RV-005: "Multi-platform aggregation pulls reviews from Google,
// Facebook, Yelp, TripAdvisor and industry-specific platforms into a unified
// review database." Registry blocker: "live write-back to Google/Facebook
// still gated by dry-run/provider mocks".
//
// Phase 1 (PR #1821) shipped the closed vocabulary + state-transition guard
// in `review-provider-write-mode.ts`. This Phase 2 file shipps the
// persistence layer: one row per (tenant_id, source_type) carrying the
// per-tenant write-back mode the future guard middleware reads before
// invoking the provider SDK.
//
// Phase 3+ (still owed):
//   - `requireReviewProviderWriteMode` middleware in `workers/api-crm/src/
//     routes/gbp.ts` + reviews routes that reads from this table
//   - Provider-SDK refusal guard surfacing `REVIEW_PROVIDER_WRITE_REFUSAL_
//     REASONS` to the UI
//   - Operator-only mode-flip route emitting `review.provider_write_mode_
//     changed@v1`
//   - Audit-trail rows for every write attempt
//
// Design rules locked here:
//   - One row per (tenant_id, source_type). Unique index enforces no
//     duplicate per-provider modes per tenant — the operator-flip route MUST
//     upsert.
//   - `default_mode` is `dry_run` per the safety-ladder forcing function in
//     Phase 1; first-connect inserts MUST honour this default.
//   - CHECK constraints inline the closed vocabulary literals to fail-closed
//     at the DB layer; future vocabulary changes MUST drop+re-add the CHECK
//     in a fresh migration.
//   - GDPR posture: no personal data (per-tenant configuration), cascade by
//     tenant on org delete via the org-membership snapshot.
// ---------------------------------------------------------------------------

import {
  check,
  index,
  pgTable,
  text,
  timestamp,
  uniqueIndex,
  uuid,
} from "drizzle-orm/pg-core";
import { sql } from "drizzle-orm";
import {
  REVIEW_PROVIDER_WRITE_MODES,
  REVIEW_SOURCE_TYPES_FOR_WRITE_MODE,
} from "./review-provider-write-mode";

const tz = { withTimezone: true } as const;

/**
 * Default first-connect write mode per the RV-005 safety ladder.
 * Operator MUST explicitly flip to `shadow` then `live` — see
 * `isLegalReviewProviderWriteModeTransition` in the vocabulary file.
 */
export const TENANT_REVIEW_PROVIDER_DEFAULT_MODE = "dry_run" as const;

export const tenantReviewProviderModes = pgTable(
  "tenant_review_provider_modes",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    /**
     * The tenant the per-provider mode belongs to. The guard middleware
     * scopes every SELECT by this column.
     */
    tenantId: uuid("tenant_id").notNull(),
    /**
     * Review-source identifier matching `REVIEW_SOURCE_TYPES_FOR_WRITE_MODE`.
     * `google_business_profile` + `facebook_pages` shipped today; Yelp +
     * TripAdvisor reserved for RV-B+.
     */
    sourceType: text("source_type").notNull(),
    /**
     * Current write-back mode. One of `REVIEW_PROVIDER_WRITE_MODES`.
     * Inserts MUST default to `TENANT_REVIEW_PROVIDER_DEFAULT_MODE`.
     */
    mode: text("mode")
      .notNull()
      .default(TENANT_REVIEW_PROVIDER_DEFAULT_MODE),
    /**
     * User who last flipped the mode. NULL for the initial first-connect
     * insert (system-assigned default). Populated by the operator-only
     * mode-flip route.
     */
    lastChangedByUserId: text("last_changed_by_user_id"),
    /**
     * When the current `mode` was set. Defaults to row creation. The
     * mode-flip route bumps this on every legal transition; idempotent
     * self-loops (e.g. `live → live`) MAY skip the bump per audit policy.
     */
    modeChangedAt: timestamp("mode_changed_at", tz).notNull().defaultNow(),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
  },
  (table) => [
    /**
     * Identity: one row per (tenant, provider). Upsert key for the
     * operator-flip route + the first-connect handler.
     */
    uniqueIndex("tenant_review_provider_modes_tenant_source_uq").on(
      table.tenantId,
      table.sourceType,
    ),
    /**
     * Per-tenant scan hot path: the guard middleware reads all modes for a
     * tenant on every request (small cardinality — 2-4 providers per
     * tenant) so a tenant-prefix index is sufficient.
     */
    index("tenant_review_provider_modes_tenant_idx").on(table.tenantId),
    /**
     * Closed-mode CHECK — fails closed at the DB layer if a future caller
     * ever writes a literal outside the vocabulary. Mirrors the order in
     * Phase 1 so the audit serializer + UI badge enumeration stay aligned.
     */
    check(
      "tenant_review_provider_modes_mode_ck",
      sql`${table.mode} IN (${sql.join(
        REVIEW_PROVIDER_WRITE_MODES.map((m) => sql`${m}`),
        sql`, `,
      )})`,
    ),
    /**
     * Closed-source CHECK — same fail-closed rationale. Yelp + TripAdvisor
     * are reserved slots from Phase 1 so a future plugin landing does not
     * need a new migration to widen the CHECK.
     */
    check(
      "tenant_review_provider_modes_source_ck",
      sql`${table.sourceType} IN (${sql.join(
        REVIEW_SOURCE_TYPES_FOR_WRITE_MODE.map((s) => sql`${s}`),
        sql`, `,
      )})`,
    ),
  ],
);

export type TenantReviewProviderMode =
  typeof tenantReviewProviderModes.$inferSelect;
export type NewTenantReviewProviderMode =
  typeof tenantReviewProviderModes.$inferInsert;
